/**
 * Eine Zeile in der ContractsUploadModal-Multi-File-Table.
 * Required Felder: type, counterparty, startDate. Optional: monthlyAmount, endDate.
 */
import { Input, Select } from '@/shared/components';
import { FileBadge, RowStatusOrRemove } from './AkteUploadAffordances';
import type { ContractRow } from './contractsUploadModel';

interface Props {
  row: ContractRow;
  defaultCurrency: string;
  onChange: (patch: Partial<ContractRow>) => void;
  onRemove: () => void;
  disabled: boolean;
}

const COMMON_TYPES = [
  'Strom',
  'Gas',
  'Wasser',
  'Internet',
  'Müll',
  'Versicherung',
  'Miete',
  'Wartung',
  'Sonstiges',
];

export function ContractsUploadRow({ row, defaultCurrency, onChange, onRemove, disabled }: Props) {
  return (
    <tr className="border-line/40 border-b align-top last:border-b-0">
      <td className="py-2 pr-3">
        <div className="flex items-start gap-2">
          <FileBadge file={row.file} />
          <div className="min-w-0">
            <div className="text-ink truncate text-xs font-medium">{row.file.name}</div>
            <div className="text-ink-faint mt-0.5 text-[10px]">
              {(row.file.size / 1024).toFixed(0)} KB
              {row.error && <span className="text-sealwax ml-2">{row.error}</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="py-2 pr-2">
        <Input
          list="contract-types"
          value={row.type}
          onChange={(e) => onChange({ type: e.target.value })}
          placeholder="Strom, Gas, ..."
          disabled={disabled}
          className="py-1.5! text-xs"
        />
        <datalist id="contract-types">
          {COMMON_TYPES.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </td>
      <td className="py-2 pr-2">
        <Input
          value={row.counterparty}
          onChange={(e) => onChange({ counterparty: e.target.value })}
          placeholder="Vodafone"
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="py-2 pr-2">
        <div className="flex gap-1">
          <Input
            type="number"
            step="0.01"
            value={row.monthlyAmount}
            onChange={(e) => onChange({ monthlyAmount: e.target.value })}
            placeholder="29,99"
            disabled={disabled}
            className="py-1.5! text-xs"
          />
          <Select
            value={row.currency || defaultCurrency}
            onChange={(e) => onChange({ currency: e.target.value })}
            disabled={disabled}
            className="w-20! py-1.5! text-xs"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="CHF">CHF</option>
          </Select>
        </div>
      </td>
      <td className="py-2 pr-2">
        <Input
          type="date"
          value={row.startDate}
          onChange={(e) => onChange({ startDate: e.target.value })}
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="py-2 pr-2">
        <Input
          type="date"
          value={row.endDate}
          onChange={(e) => onChange({ endDate: e.target.value })}
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="w-12 py-2 text-center">
        <RowStatusOrRemove status={row.status} onRemove={onRemove} disabled={disabled} />
      </td>
    </tr>
  );
}
